import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import { Modules } from "config";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import EnumRepository from "repositories/enumRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import RoadType from "entities/roadType";
import type Road from "entities/road";
import { RoadValidationRules } from "entities/road";
import OptionsService from "services/optionsService";
import EditableDetailViewModel from "viewModels/editableDetailViewModel";

@Router.registerRoute({ name: Router.Self, route: ":roadId" })
export default class RoadDetailViewModel extends EditableDetailViewModel<Road> {
  readonly modelName = "road";

  @computed
  get roadTypes() {
    return this.optionsService.internalEnumOptions(RoadType);
  }

  constructor(
    protected itemId: string,
    protected repository: EnumRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected optionsService: OptionsService
  ) {
    super(itemId);
  }

  protected async loadDetail(): Promise<Road> {
    const item = await this.repository.get<Road>(this.itemId, "roads");
    attachAutomaticValidator(item, RoadValidationRules);

    return item;
  }

  updateData(): Promise<Road> {
    return this.repository.update<Road>(this.item, "roads");
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `roads.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new RoadDetailViewModel(
        itemId,
        container.get(EnumRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(OptionsService)
      );
  }
}
